import { Home, FileText, ClipboardList, BarChart3 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { UserRole } from '@/types';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  roles: UserRole[];
}

const navItems: NavItem[] = [
  { id: 'home', label: 'Home', icon: <Home size={20} />, path: '/dashboard', roles: ['admin', 'manager', 'reviewer', 'associate'] },
  { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={20} />, path: '/analytics', roles: ['admin', 'manager'] },
  { id: 'interviews', label: 'Interviews', icon: <FileText size={20} />, path: '/interviews', roles: ['admin', 'manager', 'reviewer'] },
  { id: 'reviews', label: 'Reviews', icon: <ClipboardList size={20} />, path: '/review-queue', roles: ['admin', 'manager', 'reviewer'] },
];

export const MobileNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { vibrate } = useHapticFeedback();
  const { user } = useAuthStore();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  const handleNavigation = (path: string) => {
    vibrate('light');
    navigate(path);
  };

  // Filter navigation items based on user role
  const filteredNavItems = navItems.filter((item) =>
    user?.role ? item.roles.includes(user.role) : false
  );

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-50 lg:hidden ${
        isDark ? 'mm-glass border-t border-white/10' : 'bg-white/95 border-t border-gray-200 shadow-lg backdrop-blur-md'
      }`}
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div className="flex items-center justify-around px-1 py-1 safe-area-bottom">
        {filteredNavItems.map((item) => {
          const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');

          return (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`flex flex-col items-center justify-center gap-0.5 rounded-xl px-3 py-2.5 min-w-[60px] min-h-[52px] touch-target-lg transition-all active:scale-95 ${
                isDark
                  ? isActive
                    ? 'bg-white/10 text-violet-300'
                    : 'text-white/60 hover:text-white/80 hover:bg-white/5 active:bg-white/10'
                  : isActive
                    ? 'bg-violet-100 text-violet-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200'
              }`}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className={`transition-transform ${isActive ? 'scale-110' : ''}`}>{item.icon}</span>
              <span className="text-[11px] font-medium leading-tight mt-0.5">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
